import { html, type TemplateResult } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { ExtBaseElement } from '../../core/ExtBaseElement';
import './ext-input.css';

export type ExtInputType = 'text' | 'number' | 'email' | 'password' | 'tel';
export type ExtInputSize = 'sm' | 'md' | 'lg';

/**
 * <ext-input type="text" label="Nombre" placeholder="..." icon="user" hint="Ayuda"/>
 *
 * Input de texto con label, helper text y estados. Light DOM.
 *
 * Este componente SI tiene render() con template porque el usuario NO
 * inyecta children: todo va por props. El template produce:
 *   label + wrapper(icono + <input> + suffix) + helper(error|hint)
 * y el <input> interno emite los eventos nativos, que re-emitimos con
 * namespace ext:*.
 *
 * Selectors namespaced (.ext-input__*) para evitar colisiones en Light DOM.
 *
 * Props: type, value, placeholder, label, required, disabled, readonly,
 *   error, hint, size, icon, suffix, name, min, max, step, maxlength,
 *   autocomplete.
 *
 * Eventos:
 *  - ext:input  - { value } en cada keystroke
 *  - ext:change - { value } on blur si cambio
 *  - ext:blur   - { value }
 *  - ext:focus  - {}
 */
@customElement('ext-input')
export class ExtInput extends ExtBaseElement {
  @property({ type: String, reflect: true }) type: ExtInputType = 'text';
  @property({ type: String }) value = '';
  @property({ type: String }) placeholder = '';
  @property({ type: String }) label = '';
  @property({ type: Boolean, reflect: true }) required = false;
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) readonly = false;
  @property({ type: String, reflect: true }) error = '';
  @property({ type: String }) hint = '';
  @property({ type: String, reflect: true }) size: ExtInputSize = 'md';
  @property({ type: String }) icon = '';
  @property({ type: String }) suffix = '';
  @property({ type: String }) name = '';
  @property({ type: String }) min = '';
  @property({ type: String }) max = '';
  @property({ type: String }) step = '';
  @property({ type: Number }) maxlength: number | null = null;
  @property({ type: String }) autocomplete = '';

  @query('input') private _inputEl?: HTMLInputElement;

  private _lastEmittedValue = '';

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this._lastEmittedValue = this.value;
  }

  /** Foco programatico. */
  focus(): void {
    this._inputEl?.focus();
  }

  /** Blur programatico. */
  blur(): void {
    this._inputEl?.blur();
  }

  private _onInput = (ev: Event): void => {
    const v = (ev.target as HTMLInputElement).value;
    this.value = v;
    this._emit('input', { value: v });
  };

  private _onChange = (ev: Event): void => {
    const v = (ev.target as HTMLInputElement).value;
    this.value = v;
    if (v !== this._lastEmittedValue) {
      this._lastEmittedValue = v;
      this._emit('change', { value: v });
    }
  };

  private _onBlur = (ev: FocusEvent): void => {
    const v = (ev.target as HTMLInputElement).value;
    this._emit('blur', { value: v });
  };

  private _onFocus = (): void => {
    this._emit('focus');
  };

  render(): TemplateResult {
    const hasError = !!this.error;
    const helperText = hasError ? this.error : this.hint;

    return html`
      ${this.label
        ? html`<label class="ext-input__label">
            ${this.label}${this.required ? html`<span class="ext-input__req" aria-hidden="true">*</span>` : null}
          </label>`
        : null}
      <div class="ext-input__wrapper">
        ${this.icon
          ? html`<ext-icon class="ext-input__icon" name="${this.icon}" size="sm" aria-hidden="true"></ext-icon>`
          : null}
        <input
          class="ext-input__field"
          .type=${this.type}
          .value=${this.value}
          placeholder=${this.placeholder || ''}
          name=${this.name || ''}
          ?required=${this.required}
          ?disabled=${this.disabled}
          ?readonly=${this.readonly}
          min=${this.min || ''}
          max=${this.max || ''}
          step=${this.step || ''}
          maxlength=${this.maxlength ?? ''}
          autocomplete=${this.autocomplete || ''}
          aria-invalid=${hasError ? 'true' : 'false'}
          @input=${this._onInput}
          @change=${this._onChange}
          @blur=${this._onBlur}
          @focus=${this._onFocus}
        />
        ${this.suffix ? html`<span class="ext-input__suffix">${this.suffix}</span>` : null}
      </div>
      ${helperText
        ? html`<div class="ext-input__helper ${hasError ? 'is-error' : ''}">${helperText}</div>`
        : null}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ext-input': ExtInput;
  }
}
